import { useMutation, useQuery } from '@tanstack/react-query';
import { api, toApiError, type ApiErrorShape } from '@/api/client';
import { queryKeys } from '@/api/queryClient';
import type { components } from '@/api/schema';

export type PaymentPackage = components['schemas']['PaymentPackage'];
type CreatePaymentResponse = components['schemas']['CreatePaymentResponse'];

function assertNoError<T>(data: T | undefined, err: unknown, status: number): asserts data is T {
  if (err !== undefined || data === undefined) {
    throw toApiError(err, status);
  }
}

export function formatPackagePrice(amount: number, currency: string): string {
  const formatted = new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 0,
  }).format(amount);
  const nbsp = '\u00A0';
  return currency === 'RUB' ? `${formatted}${nbsp}₽` : `${formatted}${nbsp}${currency}`;
}

export function usePaymentPackages() {
  return useQuery({
    queryKey: queryKeys.paymentPackages,
    queryFn: async (): Promise<PaymentPackage[]> => {
      const { data, error, response } = await api.GET('/api/v1/payments/packages');
      assertNoError(data, error, response.status);
      return data;
    },
  });
}

export function useCreatePayment() {
  return useMutation<CreatePaymentResponse, ApiErrorShape, { packageId: PaymentPackage['id'] }>({
    mutationFn: async ({ packageId }) => {
      const { data, error, response } = await api.POST('/api/v1/payments/robokassa', {
        body: { packageId },
      });
      assertNoError(data, error, response.status);
      return data;
    },
  });
}
